import { createContext, useCallback, useContext, useMemo, useRef } from "react";
import type { SupabaseSession } from "../../../lib/supabase";
import { getStreetsForMunicipality } from "./streetRepository";
import type { StreetLoadResult } from "./types";

type MarketLocationController = {
  loadStreets: (municipalityId: string) => Promise<StreetLoadResult>;
};

const MarketLocationContext = createContext<MarketLocationController | null>(null);

export const useMarketLocation = () => {
  const value = useContext(MarketLocationContext);
  if (!value) throw new Error("MarketLocationContext nie je dostupný.");
  return value;
};

type MarketLocationProviderProps = {
  session: SupabaseSession | null;
  onSessionChange?: (session: SupabaseSession) => void;
  children: React.ReactNode;
};

export function MarketLocationProvider({ session, onSessionChange, children }: MarketLocationProviderProps) {
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const loadStreets = useCallback(async (municipalityId: string) => {
    const result = await getStreetsForMunicipality(municipalityId, sessionRef.current);
    if (result.session) {
      sessionRef.current = result.session;
      onSessionChange?.(result.session);
    }
    return {
      streets: result.streets,
      source: result.source,
      offline: result.offline
    };
  }, [onSessionChange]);

  const value = useMemo(() => ({ loadStreets }), [loadStreets]);
  return <MarketLocationContext.Provider value={value}>{children}</MarketLocationContext.Provider>;
}
